import React, { useState, useEffect } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SlotManagement = () => {
  const [slotName, setSlotName] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedSlotOption, setSelectedSlotOption] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Generate all times from 9:00 AM to 6:00 PM in 15-min steps
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 9; hour <= 18; hour++) {
      const minutes = hour === 18 ? [0] : [0, 15, 30, 45];
      for (let min of minutes) {
        const period = hour < 12 ? "AM" : "PM";
        const displayHour = hour > 12 ? hour - 12 : hour;
        const displayMin = min.toString().padStart(2, "0");
        times.push(`${displayHour}:${displayMin} ${period}`);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Function to extract slot number from slot name (e.g., "Slot 5" -> 5)
  const getSlotNumber = (slotName) => {
    const match = slotName.match(/Slot (\d+)/);
    return match ? parseInt(match[1]) : 999;
  };

  // Generate slot number options based on existing slots
  const generateSlotOptions = () => {
    const options = [];
    const existingSlotNumbers = slots.map(slot => getSlotNumber(slot.slot));
    
    // Generate options from 1 to 10
    for (let i = 1; i <= 10; i++) {
      const isAvailable = !existingSlotNumbers.includes(i) || 
        (editingId && getSlotNumber(slots.find(s => s.id === editingId)?.slot) === i);
      
      options.push({
        value: `Slot ${i}`,
        label: `Slot ${i}`,
        disabled: !isAvailable
      });
    }
    
    return options;
  };

  const slotOptions = generateSlotOptions();

  const notifySuccess = (msg) =>
    toast.success(msg, { autoClose: 2000, pauseOnHover: false, toastId: "success-toast" });
  const notifyError = (msg) =>
    toast.error(msg, { autoClose: 2000, pauseOnHover: false, toastId: "error-toast" });

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "slots"));
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      
      // Sort slots by slot number (Slot 1, Slot 2, Slot 3, etc.)
      const sortedList = list.sort((a, b) => {
        const numA = getSlotNumber(a.slot);
        const numB = getSlotNumber(b.slot);
        return numA - numB;
      });
      
      setSlots(sortedList);
    } catch (error) {
      console.error(error);
      notifyError("Failed to fetch slots!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlots(); }, []);

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!selectedSlotOption || !selectedTime) {
      notifyError("Please select a slot number and time!");
      return;
    }
    
    // Check if slot already exists (for add mode)
    if (!editingId) {
      const slotExists = slots.some(s => s.slot === selectedSlotOption);
      if (slotExists) {
        notifyError(`This slot number is already taken! Please select another slot.`);
        return;
      }
    }
    
    // Check if time slot is already taken (for add mode)
    if (!editingId) {
      const timeExists = slots.some(s => s.time === selectedTime);
      if (timeExists) {
        notifyError("This time slot is already taken! Please select another time.");
        return;
      }
    }
    
    try {
      if (editingId) {
        const slotRef = doc(db, "slots", editingId);
        await updateDoc(slotRef, { slot: selectedSlotOption, time: selectedTime });
        await fetchSlots(); // Refresh the list to maintain order
        notifySuccess("Slot updated successfully!");
        setEditingId(null);
      } else {
        const newSlot = { slot: selectedSlotOption, time: selectedTime, createdAt: serverTimestamp() };
        await addDoc(collection(db, "slots"), newSlot);
        await fetchSlots(); // Refresh the list to maintain order
        notifySuccess("Slot added successfully!");
      }
      setSelectedSlotOption("");
      setSelectedTime("");
      setSlotName("");
    } catch (error) {
      console.error(error);
      notifyError("Failed to save slot!");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "slots", id));
      await fetchSlots(); // Refresh the list to maintain order
      toast.info("Slot deleted successfully!", { autoClose: 2000, toastId: "delete-toast" });
    } catch (error) {
      console.error(error);
      notifyError("Failed to delete slot!");
    }
  };

  const handleEdit = (slot) => {
    setSelectedSlotOption(slot.slot);
    setSelectedTime(slot.time);
    setEditingId(slot.id);
    notifySuccess(`Editing ${slot.slot}`);
  };

  return (
    <div className="slot-page">
      <h1 className="page-heading">
        <i className="fas fa-clock"></i> Slot Management
      </h1>
      <form onSubmit={handleAddOrUpdate} className="slot-form">
        <div className="form-inner">
          <div className="form-group">
            <label>Select Slot Number</label>
            <select
              value={selectedSlotOption}
              onChange={(e) => setSelectedSlotOption(e.target.value)}
            >
              <option value="">Select Slot Number</option>
              {slotOptions.map((option, index) => (
                <option key={index} value={option.value} disabled={option.disabled}>
                  {option.label} {option.disabled && "(Already Taken)"}
                </option>
              ))}
            </select>
            <small style={{ color: "#666", fontSize: "12px", display: "block", marginTop: "5px" }}>
              Available slots: Slot 1 to Slot 10. Only empty slots will be shown.
            </small>
          </div>
          <div className="form-group">
            <label>Select Time</label>
            <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
            >
              <option value="">Select Time</option>
              {timeOptions.map((time, index) => (
                <option key={index} value={time}>{time}</option>
              ))}
            </select>
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={loading}>
          <i className="fas fa-save"></i>{" "}
          {editingId ? "Update Slot" : "Save Slot"}
        </button>
      </form>

      <div className="slot-list">
        <h2>Saved Slots</h2>
        {loading ? (
          <LoadingAnimation />
        ) : slots.length === 0 ? (
          <p>No slots added yet.</p>
        ) : (
          <ul>
            {slots.map((slot) => (
              <li key={slot.id} style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "space-between" }}>
                <span>
                  <strong>{slot.slot}:</strong> {slot.time}
                </span>
                <div className="button">
                  <button className="btn-edit" onClick={() => handleEdit(slot)}>
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(slot.id)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ToastContainer position="top-right" newestOnTop limit={1} closeOnClick draggable={false} />
    </div>
  );
};

export default SlotManagement;